import { useEffect, useRef, useState } from 'preact/hooks';

/** Sparkline that draws itself in. */
export function Spark({ data, color = 'var(--brand)', fill = true, height = 34, endDot = true }) {
  const w = 200, h = height, min = Math.min(...data), max = Math.max(...data), r = max - min || 1;
  const pts = data.map((v, i) => [i / (data.length - 1) * w, h - 3 - (v - min) / r * (h - 8)]);
  const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg class="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      {fill && <path d={`${d} L${w} ${h} L0 ${h} Z`} fill={color} opacity=".08" />}
      <path d={d} fill="none" stroke={color} stroke-width="2" vector-effect="non-scaling-stroke" pathLength="1" style="stroke-dasharray:1;stroke-dashoffset:1;animation:draw 1.2s .2s ease-out forwards" />
      {endDot && <circle cx={last[0]} cy={last[1]} r="3" fill={color} vector-effect="non-scaling-stroke" />}
      <style>{'@keyframes draw{to{stroke-dashoffset:0}}'}</style>
    </svg>
  );
}

/** Number that counts up on first render, then follows its value. */
export function CountUp({ value, decimals = 0, locale = 'en', duration = 1100 }) {
  const [v, setV] = useState(0); const first = useRef(true); const from = useRef(0);
  useEffect(() => {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setV(value); return; }
    const a = first.current ? 0 : from.current, t0 = performance.now(), dur = first.current ? duration : 400; first.current = false;
    let raf; const step = t => { const p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3); setV(a + (value - a) * e); if (p < 1) raf = requestAnimationFrame(step); else from.current = value; };
    raf = requestAnimationFrame(step); return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{v.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</>;
}

/** Text that types itself out (AI answers). */
export function Typewriter({ text, speed = 14, onDone, start = true }) {
  const [n, setN] = useState(0);
  useEffect(() => { setN(0); }, [text]);
  useEffect(() => {
    if (!start) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { setN(text.length); onDone?.(); return; }
    if (n >= text.length) { onDone?.(); return; }
    const id = setTimeout(() => setN(x => Math.min(text.length, x + 1 + Math.floor(Math.random() * 3))), speed);
    return () => clearTimeout(id);
  }, [n, text, start]);
  return <>{text.slice(0, n)}{n < text.length && <span class="caret" />}</>;
}
